import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
  useMediaQuery,
  useTheme,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ScienceIcon from '@mui/icons-material/Science';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpIcon from '@mui/icons-material/Help';
import CodeIcon from '@mui/icons-material/Code';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BoltIcon from '@mui/icons-material/Bolt';
import HelpDialog from '../HelpDialog/HelpDialog';
import { exportToExcel, exportAnalysisReport, generateAnalysisText, exportCompleteRunToExcel } from '../../utils/exportUtils';
import { useOptimization } from '../../context/OptimizationContext';
import { EnergyAwareWeights, OptimizationParameters } from '../../types';
import { NotificationSnackbar } from '../common/NotificationSnackbar';
import ParameterPanel from '../ParameterPanel/ParameterPanel';
import DatasetUpload from '../DatasetUpload/DatasetUpload';
import OptimizationProgress from '../OptimizationProgress/OptimizationProgress';
import ResultsView from '../ResultsView/ResultsView';
import ChartsView from '../ChartsView/ChartsView';
import HistoryView from '../HistoryView/HistoryView';
import EnergyLeaderboard from '../EnergyLeaderboard/EnergyLeaderboard';
import CombinedWinnerLeaderboard from '../CombinedWinnerLeaderboard/CombinedWinnerLeaderboard';
import Footer from './Footer';
import CompleteRunModal from '../CompleteRunModal/CompleteRunModal';
import AlgorithmsModal from '../AlgorithmsModal/AlgorithmsModal';
import NavigationSearchDialog, { NavigationTarget } from '../NavigationSearch/NavigationSearchDialog';
import SimulationPanel, { EnergySimulationPanel } from '../SimulationModal/SimulationModal';
import SflagaImprovementBenchmarkModal from '../SflagaImprovementBenchmarkModal/SflagaImprovementBenchmarkModal';
import settings from '../../config/settings';
import { resolveSflagaEnhancements } from '../../utils/parameterUtils';
import { generateFreshMatrixSeed, resolveMatrixSeed } from '../../utils/qwsParser';

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isRunning, startOptimization, startCompleteRun, cancelOptimization, results, notification, showNotification, hideNotification } = useOptimization();
  const parametersRef = useRef<OptimizationParameters | null>(null);
  const datasetRef = useRef<string>('');
  const [currentParameters, setCurrentParameters] = useState<OptimizationParameters | null>(null);
  const [completeRunModalOpen, setCompleteRunModalOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [algorithmsModalOpen, setAlgorithmsModalOpen] = useState(false);
  const [benchmarkModalCode, setBenchmarkModalCode] = useState<'g' | 'j' | null>(null);
  const [navigationSearchOpen, setNavigationSearchOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
  const [activeResultsTab, setActiveResultsTab] = useState(0);
  const [activeChartsTab, setActiveChartsTab] = useState(0);
  const [simulationExpanded, setSimulationExpanded] = useState(false);
  const [energySimulationExpanded, setEnergySimulationExpanded] = useState(false);
  const resultsSectionRef = useRef<HTMLDivElement | null>(null);
  const chartsSectionRef = useRef<HTMLDivElement | null>(null);
  const historySectionRef = useRef<HTMLDivElement | null>(null);
  const menuOpen = Boolean(menuAnchorEl);
  
  const showAlgorithms = settings.showAlgorithms === 1;
  const hasStatistics = Boolean(results && 'statistics' in results && results.statistics);

  const scrollToSection = useCallback((sectionRef: { current: HTMLElement | null }) => {
    window.requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, []);

  useEffect(() => {
    if (!hasStatistics && activeResultsTab > 7) {
      setActiveResultsTab(0);
    }
  }, [activeResultsTab, hasStatistics]);

  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName;
      const isEditable = Boolean(
        target?.isContentEditable ||
          tagName === 'INPUT' ||
          tagName === 'TEXTAREA' ||
          tagName === 'SELECT'
      );

      if (isEditable) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setNavigationSearchOpen(true);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, []);

  const navigationTargets = useMemo<NavigationTarget[]>(
    () => [
      {
        id: 'results-solutions',
        title: 'Solutions',
        section: 'Results',
        description: 'Selected service indices for each algorithm.',
        destination: 'results',
        tabIndex: 0,
        keywords: ['results', 'solution', 'services'],
      },
      {
        id: 'results-fitness',
        title: 'Fitness Table',
        section: 'Results',
        description: 'Compare final fitness values in tabular form.',
        destination: 'results',
        tabIndex: 1,
        keywords: ['results', 'fitness', 'table'],
      },
      {
        id: 'results-config',
        title: 'Configuration',
        section: 'Results',
        description: 'Inspect the configuration used for the current run.',
        destination: 'results',
        tabIndex: 2,
        keywords: ['results', 'config', 'parameters', 'settings'],
      },
      {
        id: 'results-energy',
        title: 'Energy Table',
        section: 'Results',
        description: 'Review energy values side by side.',
        destination: 'results',
        tabIndex: 3,
        keywords: ['results', 'energy'],
      },
      {
        id: 'results-cost',
        title: 'Cost Table',
        section: 'Results',
        description: 'Review cost values side by side.',
        destination: 'results',
        tabIndex: 4,
        keywords: ['results', 'cost'],
      },
      {
        id: 'results-rt',
        title: 'Response Time Table',
        section: 'Results',
        description: 'Compare response time values in one place.',
        destination: 'results',
        tabIndex: 5,
        keywords: ['results', 'response time', 'rt', 'latency'],
      },
      {
        id: 'results-analysis',
        title: 'Analysis Summary',
        section: 'Results',
        description: 'See the ranked analysis summary for the current run.',
        destination: 'results',
        tabIndex: 6,
        keywords: ['results', 'analysis', 'summary'],
      },
      {
        id: 'results-combined-winner',
        title: 'Combined Winner',
        section: 'Results',
        description: 'Open the combined winner comparison tab.',
        destination: 'results',
        tabIndex: 7,
        keywords: ['results', 'combined winner', 'leader'],
      },
      {
        id: 'results-stats',
        title: 'Statistical Analysis',
        section: 'Results',
        description: 'Median, mean, standard deviation, and QTI statistics.',
        destination: 'results',
        tabIndex: 8,
        keywords: ['results', 'statistics', 'stats', 'median', 'qti'],
        disabled: !hasStatistics,
      },
      {
        id: 'charts-fitness',
        title: 'Fitness Comparison Chart',
        section: 'Charts',
        description: 'Bar chart of final fitness values.',
        destination: 'charts',
        tabIndex: 0,
        keywords: ['charts', 'fitness', 'comparison'],
      },
      {
        id: 'charts-convergence',
        title: 'Convergence',
        section: 'Charts',
        description: 'Fitness over iterations for all algorithms.',
        destination: 'charts',
        tabIndex: 2,
        keywords: ['charts', 'convergence', 'iterations'],
      },
      {
        id: 'charts-aggregated-qos',
        title: 'Aggregated QoS Chart',
        section: 'Charts',
        description: 'Workflow-aware aggregated QoS comparison across algorithms.',
        destination: 'charts',
        tabIndex: 1,
        keywords: ['charts', 'aggregated qos', 'qos', 'quality'],
      },
      {
        id: 'charts-similarity',
        title: 'Similarity Chart',
        section: 'Charts',
        description: 'Relative similarity against SFLAGA fitness.',
        destination: 'charts',
        tabIndex: 3,
        keywords: ['charts', 'similarity', 'sflaga'],
      },
      {
        id: 'charts-energy',
        title: 'Energy Chart',
        section: 'Charts',
        description: 'Energy comparison across algorithms.',
        destination: 'charts',
        tabIndex: 4,
        keywords: ['charts', 'energy'],
      },
      {
        id: 'charts-cost',
        title: 'Cost Chart',
        section: 'Charts',
        description: 'Cost comparison across algorithms.',
        destination: 'charts',
        tabIndex: 5,
        keywords: ['charts', 'cost'],
      },
      {
        id: 'charts-response-time',
        title: 'Response Time Chart',
        section: 'Charts',
        description: 'Response time comparison across algorithms.',
        destination: 'charts',
        tabIndex: 6,
        keywords: ['charts', 'response time', 'rt', 'latency'],
      },
      {
        id: 'charts-execution-time',
        title: 'Execution Time Chart',
        section: 'Charts',
        description: 'Execution time comparison across algorithms.',
        destination: 'charts',
        tabIndex: 7,
        keywords: ['charts', 'execution time', 'runtime'],
      },
      {
        id: 'charts-qti',
        title: 'QTI Chart',
        section: 'Charts',
        description: 'Derived Quality-Time Index comparison.',
        destination: 'charts',
        tabIndex: 8,
        keywords: ['charts', 'qti', 'quality-time index'],
      },
      {
        id: 'charts-normalized',
        title: 'Normalized Metrics',
        section: 'Charts',
        description: 'Normalized 0-1 comparison across major metrics.',
        destination: 'charts',
        tabIndex: 9,
        keywords: ['charts', 'normalized', 'metrics'],
      },
      {
        id: 'charts-multi-metric',
        title: 'Multi-Metric Summary',
        section: 'Charts',
        description: 'Combined multi-metric comparison view.',
        destination: 'charts',
        tabIndex: 10,
        keywords: ['charts', 'multi metric', 'summary'],
      },
      {
        id: 'charts-convergence-speed',
        title: 'Convergence Speed',
        section: 'Charts',
        description: 'Improvement rate and iterations to 90 percent.',
        destination: 'charts',
        tabIndex: 11,
        keywords: ['charts', 'convergence speed', 'improvement rate'],
      },
      {
        id: 'charts-combined-winner',
        title: 'Combined Winner Chart',
        section: 'Charts',
        description: 'Winner comparison in chart form.',
        destination: 'charts',
        tabIndex: 12,
        keywords: ['charts', 'combined winner'],
      },
      {
        id: 'charts-energy-box-plot',
        title: 'Energy Box Plot',
        section: 'Charts',
        description: 'Distribution of energy results across runs.',
        destination: 'charts',
        tabIndex: 13,
        keywords: ['charts', 'energy box plot', 'distribution', 'statistics'],
      },
      {
        id: 'history',
        title: 'Run History',
        section: 'History',
        description: 'Review, compare, and reopen past optimization runs.',
        destination: 'history',
        keywords: ['history', 'past runs', 'compare'],
      },
    ],
    [hasStatistics]
  );

  const handleNavigationSelect = useCallback(
    (target: NavigationTarget) => {
      if (target.destination === 'results') {
        setActiveResultsTab(target.tabIndex ?? 0);
        scrollToSection(resultsSectionRef);
        return;
      }

      if (target.destination === 'charts') {
        setActiveChartsTab(target.tabIndex ?? 0);
        scrollToSection(chartsSectionRef);
        return;
      }

      scrollToSection(historySectionRef);
    },
    [scrollToSection]
  );

  const handleLogout = () => {
    sessionStorage.removeItem('isAuthenticated');
    sessionStorage.removeItem('username');
    navigate('/');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleDownloadDataset = () => {
    try {
      // Create a link element and trigger download
      const link = document.createElement('a');
      link.href = '/QWS_Dataset.txt';
      link.download = 'QWS_Dataset.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showNotification('QWS Dataset downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading dataset:', error);
      showNotification('Failed to download dataset', 'error');
    }
  };

  const handleParametersChange = (params: {
    taskCount: number;
    qosProperties: number;
    iterations: number;
    initialPopulation: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
    memeplexCount: number;
    numberOfRuns?: number;
    includeSflaGaBaseline?: boolean;
    parallelExecution?: boolean;
    recordMode?: OptimizationParameters['recordMode'];
    fitnessMode?: 'qos-only' | 'energy-aware';
    energyAwareWeights?: EnergyAwareWeights;
    sflagaEnhancements?: OptimizationParameters['sflagaEnhancements'];
    parameterPolicy?: string;
    seed?: number;
    taskSpecificVariationEnabled?: boolean;
  }) => {
    const optParams: OptimizationParameters = {
      taskCount: params.taskCount,
      qosProperties: params.qosProperties,
      iterations: params.iterations,
      initialPopulation: params.initialPopulation,
      selectionRate: params.selectionRate,
      crossoverRate: params.crossoverRate,
      mutationRate: params.mutationRate,
      memeplexCount: params.memeplexCount,
      numberOfRuns: params.numberOfRuns || 1,
      includeSflaGaBaseline: params.includeSflaGaBaseline ?? false,
      parallelExecution: params.parallelExecution ?? false,
      heavyMode: false,
      recordMode: params.recordMode ?? 'off',
      fitnessMode: params.fitnessMode,
      energyAwareWeights: params.energyAwareWeights,
      sflagaEnhancements: resolveSflagaEnhancements(params.sflagaEnhancements),
      parameterPolicy: params.parameterPolicy,
      seed: params.seed ?? resolveMatrixSeed(),
      taskSpecificVariationEnabled: params.taskSpecificVariationEnabled,
    };
    parametersRef.current = optParams;
    setCurrentParameters(optParams);
  };

  const handleFileSelect = (file: File, content: string) => {
    datasetRef.current = content;
  };

  const handleSeedChange = (seed: number) => {
    setCurrentParameters((prev) => {
      const baseParams = prev ?? parametersRef.current;
      if (!baseParams) {
        return prev;
      }

      const nextParams: OptimizationParameters = {
        ...baseParams,
        seed,
      };

      parametersRef.current = nextParams;
      return nextParams;
    });
  };

  const handleStart = async () => {
    const currentParams = parametersRef.current;
    const currentDataset = datasetRef.current;

    if (!currentParams) {
      showNotification('Please configure parameters first', 'warning');
      return;
    }

    if (!currentDataset) {
      showNotification('Please select a dataset file first', 'warning');
      return;
    }

    const runSeed = generateFreshMatrixSeed(currentParams.seed);
    const runParams: OptimizationParameters = {
      ...currentParams,
      seed: runSeed,
    };

    parametersRef.current = runParams;
    setCurrentParameters(runParams);

    await startOptimization(runParams, currentDataset);
  };

  const handleExportExcel = () => {
    if (!results) {
      showNotification('No results available to export', 'warning');
      return;
    }
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `optimization_results_${timestamp}.xlsx`;
    exportToExcel(results, filename);
    showNotification('Excel file downloaded successfully', 'success');
  };

  const handleExportAnalysis = () => {
    if (!results) {
      showNotification('No results available to export', 'warning');
      return;
    }
    
    const analysisText = generateAnalysisText(results);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `analysis_report_${timestamp}.txt`;
    exportAnalysisReport(analysisText, filename);
    showNotification('Analysis report downloaded successfully', 'success');
  };

  const handleCompleteRun = async (
    workflowStart: number,
    workflowEnd: number,
    increment: number,
    numberOfRuns: number
  ) => {
    const currentParams = parametersRef.current;
    const currentDataset = datasetRef.current;

    if (!currentParams) {
      showNotification('Please configure parameters first', 'warning');
      return;
    }

    if (!currentDataset) {
      showNotification('Please select a dataset file first', 'warning');
      return;
    }

    try {
      const completeRunResults = await startCompleteRun(
        currentParams,
        currentDataset,
        workflowStart,
        workflowEnd,
        increment,
        numberOfRuns,
        (current, total, taskCount, params) => {
          // Update UI parameters to show current task count and auto-adjusted parameters
          if (params) {
            setCurrentParameters(params);
            parametersRef.current = params;
          }
          showNotification(
            `Complete Run: ${current}/${total} completed (${taskCount} tasks)`,
            'info'
          );
        }
      );

      // Export results to Excel
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `complete_run_${workflowStart}_to_${workflowEnd}_${timestamp}.xlsx`;
      exportCompleteRunToExcel(completeRunResults, filename);
      showNotification(
        `Complete run finished! ${completeRunResults.length} optimizations completed. Results exported to Excel.`,
        'success'
      );
    } catch (error) {
      console.error('Complete run error:', error);
      showNotification(
        'Complete run failed: ' + (error instanceof Error ? error.message : String(error)),
        'error'
      );
    }
  };

  const hasResults = results !== null;
  const centerMdColumns = sidebarOpen ? 8 : 12;
  const centerLgColumns = sidebarOpen && rightSidebarOpen ? 7 : sidebarOpen || rightSidebarOpen ? 9 : 12;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" elevation={2}>
        <Toolbar
          sx={{
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: { xs: 1, md: 0 },
            '& .MuiButton-root': {
              fontSize: { xs: '0.68rem', md: '0.74rem' },
            },
          }}
        >
          <ScienceIcon sx={{ mr: { xs: 1, md: 2 } }} />
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Mobile Menu */}
          {isMobile ? (
            <>
              <IconButton
                color="inherit"
                onClick={handleMenuOpen}
                sx={{ ml: 'auto' }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: { minWidth: 200 }
                }}
              >
                <MenuItem onClick={() => { handleLogout(); handleMenuClose(); }}>
                  <LogoutIcon sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
                <MenuItem onClick={() => { setBenchmarkModalCode('g'); handleMenuClose(); }}>
                  <BoltIcon sx={{ mr: 1 }} />
                  G
                </MenuItem>
                <MenuItem onClick={() => { setBenchmarkModalCode('j'); handleMenuClose(); }}>
                  <BoltIcon sx={{ mr: 1 }} />
                  J
                </MenuItem>
                <MenuItem onClick={() => { handleDownloadDataset(); handleMenuClose(); }}>
                  <FileDownloadIcon sx={{ mr: 1 }} />
                  Download Dataset
                </MenuItem>
                <MenuItem onClick={() => { setNavigationSearchOpen(true); handleMenuClose(); }}>
                  <SearchIcon sx={{ mr: 1 }} />
                  Search UI
                </MenuItem>
                <MenuItem onClick={() => { setHelpDialogOpen(true); handleMenuClose(); }}>
                  <HelpIcon sx={{ mr: 1 }} />
                  Help
                </MenuItem>
                {showAlgorithms && (
                  <MenuItem onClick={() => { setAlgorithmsModalOpen(true); handleMenuClose(); }}>
                    <CodeIcon sx={{ mr: 1 }} />
                    Algorithms
                  </MenuItem>
                )}
                <MenuItem onClick={() => { handleExportExcel(); handleMenuClose(); }} disabled={!hasResults}>
                  <FileDownloadIcon sx={{ mr: 1 }} />
                  Export Excel
                </MenuItem>
                <MenuItem onClick={() => { handleExportAnalysis(); handleMenuClose(); }} disabled={!hasResults}>
                  <AssessmentIcon sx={{ mr: 1 }} />
                  Export Analysis
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ 
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Logout
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setBenchmarkModalCode('g')}
                sx={{
                  minWidth: 42,
                  px: 1.5,
                  fontWeight: 900,
                  bgcolor: '#0f766e',
                  color: 'white',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#115e59',
                    boxShadow: 'none',
                  },
                }}
              >
                G
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setBenchmarkModalCode('j')}
                sx={{
                  minWidth: 42,
                  px: 1.5,
                  fontWeight: 900,
                  bgcolor: '#9a3412',
                  color: 'white',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#7c2d12',
                    boxShadow: 'none',
                  },
                }}
              >
                J
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleDownloadDataset}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Download Dataset
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<SearchIcon />}
                onClick={() => setNavigationSearchOpen(true)}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Search UI
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<HelpIcon />}
                onClick={() => setHelpDialogOpen(true)}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Help
              </Button>
              {showAlgorithms && (
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  startIcon={<CodeIcon />}
                  onClick={() => setAlgorithmsModalOpen(true)}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Algorithms
                </Button>
              )}
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                disabled={!hasResults}
                sx={{ 
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&.Mui-disabled': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                Export Excel
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<AssessmentIcon />}
                onClick={handleExportAnalysis}
                disabled={!hasResults}
                sx={{ 
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&.Mui-disabled': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                Export Analysis
              </Button>
            </Box>
          )}
          
          {/* Action Buttons */}
          {!isRunning ? (
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' }, mt: { xs: 1, md: 0 } }}>
              <Button
                variant="contained"
                color="secondary"
                size={isMobile ? 'medium' : 'large'}
                startIcon={<PlayArrowIcon />}
                onClick={handleStart}
                sx={{ 
                  minWidth: { xs: 'auto', md: 180 },
                  flex: { xs: 1, md: 'none' }
                }}
              >
                {isMobile ? 'Start' : 'Start Optimization'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size={isMobile ? 'medium' : 'large'}
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setCompleteRunModalOpen(true)}
                sx={{ 
                  minWidth: { xs: 'auto', md: 180 },
                  flex: { xs: 1, md: 'none' }
                }}
              >
                {isMobile ? 'Complete' : 'Complete Run'}
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              color="error"
              size={isMobile ? 'medium' : 'large'}
              startIcon={<StopIcon />}
              onClick={cancelOptimization}
              sx={{ 
                minWidth: { xs: 'auto', md: 180 },
                width: { xs: '100%', md: 'auto' },
                mt: { xs: 1, md: 0 }
              }}
            >
              {isMobile ? 'Cancel' : 'Cancel Optimization'}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: { xs: 2, md: 3 }, 
          mb: { xs: 2, md: 3 }, 
          flexGrow: 1, 
          px: { xs: 1, sm: 2, md: 2, lg: 0 } 
        }}
      >
        <Box sx={{ position: 'relative' }}>
          {/* Toggle Button - Desktop */}
          {sidebarOpen ? (
            <IconButton
              onClick={() => setSidebarOpen(false)}
              sx={{
                position: 'absolute',
                left: { md: -20 },
                top: { md: 20 },
                zIndex: 1000,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                display: { xs: 'none', md: 'flex' },
              }}
              size="small"
              aria-label="Hide sidebar"
            >
              <ChevronLeftIcon />
            </IconButton>
          ) : (
            <IconButton
              onClick={() => setSidebarOpen(true)}
              sx={{
                position: 'absolute',
                left: { md: 0 },
                top: { md: 20 },
                zIndex: 1000,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: 'none',
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                display: { xs: 'none', md: 'flex' },
              }}
              size="small"
              aria-label="Show sidebar"
            >
              <ChevronRightIcon />
            </IconButton>
          )}

          {rightSidebarOpen ? (
            <IconButton
              onClick={() => setRightSidebarOpen(false)}
              sx={{
                position: 'absolute',
                right: { md: -20 },
                top: { md: 20 },
                zIndex: 1000,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                display: { xs: 'none', lg: 'flex' },
              }}
              size="small"
              aria-label="Hide right sidebar"
            >
              <ChevronRightIcon />
            </IconButton>
          ) : (
            <IconButton
              onClick={() => setRightSidebarOpen(true)}
              sx={{
                position: 'absolute',
                right: { lg: 0 },
                top: { lg: 20 },
                zIndex: 1000,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRight: 'none',
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                display: { xs: 'none', lg: 'flex' },
              }}
              size="small"
              aria-label="Show right sidebar"
            >
              <ChevronLeftIcon />
            </IconButton>
          )}

          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            {/* Left Sidebar - Parameters and Controls */}
            {sidebarOpen && (
              <Grid 
                item 
                xs={12} 
                md={4} 
                lg={3} 
                sx={{ 
                  position: { xs: 'static', md: 'sticky' }, 
                  top: { md: 24 }, 
                  alignSelf: 'flex-start' 
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
                  {/* Toggle Button for Mobile */}
                  <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-end', mb: -1 }}>
                    <IconButton
                      onClick={() => setSidebarOpen(false)}
                      size="small"
                      sx={{ color: 'text.secondary' }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                  </Box>

                  {/* Parameter Configuration */}
                  <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
                    <ParameterPanel
                      disabled={isRunning}
                      initialValues={currentParameters ? {
                        taskCount: currentParameters.taskCount,
                        qosProperties: currentParameters.qosProperties,
                        iterations: currentParameters.iterations,
                        initialPopulation: currentParameters.initialPopulation,
                        selectionRate: currentParameters.selectionRate,
                        crossoverRate: currentParameters.crossoverRate,
                        mutationRate: currentParameters.mutationRate,
                        memeplexCount: currentParameters.memeplexCount,
                        numberOfRuns: currentParameters.numberOfRuns,
                        includeSflaGaBaseline: currentParameters.includeSflaGaBaseline,
                        parallelExecution: currentParameters.parallelExecution,
                        recordMode: currentParameters.recordMode,
                        fitnessMode: currentParameters.fitnessMode,
                        energyAwareWeights: currentParameters.energyAwareWeights,
                        sflagaEnhancements: currentParameters.sflagaEnhancements,
                        parameterPolicy: currentParameters.parameterPolicy,
                        seed: currentParameters.seed,
                        taskSpecificVariationEnabled: currentParameters.taskSpecificVariationEnabled,
                      } : undefined}
                      onParametersChange={handleParametersChange}
                    />
                    {isRunning && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        Sidebar controls are disabled while optimization is running.
                      </Typography>
                    )}
                  </Paper>

                  {/* Dataset Upload */}
                  <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
                    <DatasetUpload
                      disabled={isRunning}
                      onFileSelect={handleFileSelect}
                      onError={(msg) => showNotification(msg, 'error')}
                      taskCount={currentParameters?.taskCount ?? 1}
                      seed={currentParameters?.seed}
                      onSeedChange={handleSeedChange}
                      taskSpecificVariationEnabled={currentParameters?.taskSpecificVariationEnabled ?? true}
                      onTaskSpecificVariationChange={(taskSpecificVariationEnabled) => {
                        setCurrentParameters((prev) => {
                          const baseParams = prev ?? parametersRef.current;
                          if (!baseParams) {
                            return prev;
                          }

                          const nextParams: OptimizationParameters = {
                            ...baseParams,
                            taskSpecificVariationEnabled,
                          };

                          parametersRef.current = nextParams;
                          return nextParams;
                        });
                      }}
                    />
                  </Paper>

                </Box>
              </Grid>
            )}

            {/* Show Toggle Button when Sidebar is Hidden (Mobile) */}
            {!sidebarOpen && (
              <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%', mb: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ChevronRightIcon />}
                  onClick={() => setSidebarOpen(true)}
                  fullWidth
                  size="small"
                >
                  Show Parameters Panel
                </Button>
              </Box>
            )}

            {/* Main Content Area - Results and Charts */}
            <Grid item xs={12} md={centerMdColumns} lg={centerLgColumns}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
              {/* Convergence Live Simulation Section */}
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  position: 'relative',
                  overflow: 'hidden',
                  background: 'radial-gradient(140% 120% at 15% 20%, rgba(45,212,191,0.12), transparent), linear-gradient(135deg, #ffffff 0%, #f8fafc 55%, #f1f5f9 100%)',
                  color: '#0f172a',
                  border: '1px solid rgba(15,23,42,0.08)',
                  boxShadow: '0 18px 42px rgba(15,23,42,0.08)',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: '-40% -10% auto auto',
                    width: 240,
                    height: 240,
                    background: 'radial-gradient(circle at center, rgba(45,212,191,0.1), transparent 70%)',
                    transform: 'rotate(12deg)',
                  },
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                    }}
                    onClick={() => setSimulationExpanded((prev) => !prev)}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        color: '#0f766e',
                        background: 'linear-gradient(135deg, rgba(45,212,191,0.18), rgba(45,212,191,0.06))',
                        border: '1px solid rgba(13,148,136,0.24)',
                      }}
                    >
                      <ShowChartIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                        Convergence Live Simulation
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        Watch fitness curves update in real time while algorithms compete.
                      </Typography>
                    </Box>
                    <Chip
                      label={isRunning ? 'Live stream' : 'Paused'}
                      color={isRunning ? 'success' : 'default'}
                      size="small"
                      sx={{
                        color: isRunning ? '#166534' : '#475569',
                        backgroundColor: isRunning ? 'rgba(34,197,94,0.12)' : 'rgba(148,163,184,0.12)',
                        border: `1px solid ${isRunning ? 'rgba(34,197,94,0.2)' : 'rgba(148,163,184,0.22)'}`,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        fontWeight: 700,
                      }}
                    />
                    {simulationExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                  <Collapse in={simulationExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 2 }}>
                      <SimulationPanel />
                    </Box>
                  </Collapse>
                </Box>
              </Paper>

              {/* Energy Simulation Section */}
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  position: 'relative',
                  overflow: 'hidden',
                  background: 'radial-gradient(130% 120% at 18% 18%, rgba(252,211,77,0.14), transparent), linear-gradient(140deg, #ffffff 0%, #fffdf7 55%, #f8fafc 100%)',
                  color: '#0f172a',
                  border: '1px solid rgba(15,23,42,0.08)',
                  boxShadow: '0 18px 42px rgba(15,23,42,0.08)',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: '-46% auto auto -14%',
                    width: 220,
                    height: 220,
                    background: 'radial-gradient(circle at center, rgba(251,191,36,0.12), transparent 70%)',
                    transform: 'rotate(-8deg)',
                  },
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                    }}
                    onClick={() => setEnergySimulationExpanded((prev) => !prev)}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        color: '#b45309',
                        background: 'linear-gradient(135deg, rgba(252,211,77,0.2), rgba(251,191,36,0.07))',
                        border: '1px solid rgba(217,119,6,0.24)',
                      }}
                    >
                      <BoltIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                        Energy Live Simulation
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        Track energy totals as soon as each algorithm completes.
                      </Typography>
                    </Box>
                    <Chip
                      label={isRunning ? 'Live stream' : 'Paused'}
                      color={isRunning ? 'success' : 'default'}
                      size="small"
                      sx={{
                        color: isRunning ? '#166534' : '#475569',
                        backgroundColor: isRunning ? 'rgba(34,197,94,0.12)' : 'rgba(148,163,184,0.12)',
                        border: `1px solid ${isRunning ? 'rgba(34,197,94,0.2)' : 'rgba(148,163,184,0.22)'}`,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        fontWeight: 700,
                      }}
                    />
                    {energySimulationExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                  <Collapse in={energySimulationExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 2 }}>
                      <EnergySimulationPanel />
                    </Box>
                  </Collapse>
                </Box>
              </Paper>

              {/* Optimization Progress */}
              <OptimizationProgress />

              {/* Results Tabs */}
              <Paper ref={resultsSectionRef} elevation={2} sx={{ p: { xs: 1.5, md: 2 } }}>
                <ResultsView
                  activeTab={activeResultsTab}
                  onTabChange={setActiveResultsTab}
                />
              </Paper>

              {/* Charts */}
              <Paper ref={chartsSectionRef} elevation={2} sx={{ p: { xs: 1.5, md: 2 } }}>
                <ChartsView
                  activeTab={activeChartsTab}
                  onTabChange={setActiveChartsTab}
                />
              </Paper>

              {/* History */}
              <Paper ref={historySectionRef} elevation={2} sx={{ p: { xs: 1.5, md: 2 } }}>
                <HistoryView />
              </Paper>
            </Box>
          </Grid>

          {!rightSidebarOpen && (
            <Box sx={{ display: { xs: 'block', lg: 'none' }, width: '100%', mb: 1 }}>
              <Button
                variant="outlined"
                endIcon={<ChevronLeftIcon />}
                onClick={() => setRightSidebarOpen(true)}
                fullWidth
                size="small"
              >
                Show Right Panel
              </Button>
            </Box>
          )}

          {/* Right Sidebar - Energy Leaderboard and Combined Winner */}
          {rightSidebarOpen && (
            <Grid item xs={12} md={12} lg={sidebarOpen ? 2 : 3}>
              <Box
                sx={{
                  position: { xs: 'static', md: 'sticky' },
                  top: { md: 24 },
                  alignSelf: 'flex-start',
                }}
              >
                <Box sx={{ display: { xs: 'flex', lg: 'none' }, justifyContent: 'flex-end', mb: -1 }}>
                  <IconButton
                    onClick={() => setRightSidebarOpen(false)}
                    size="small"
                    sx={{ color: 'text.secondary' }}
                    aria-label="Hide right sidebar"
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
                <EnergyLeaderboard />
                <CombinedWinnerLeaderboard />
              </Box>
            </Grid>
          )}
        </Grid>
        </Box>
      </Container>

      {/* Footer */}
      <Footer />

      {/* Notification Snackbar */}
      {notification && (
        <NotificationSnackbar
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={hideNotification}
        />
      )}

      {/* Complete Run Modal */}
      <CompleteRunModal
        open={completeRunModalOpen}
        onClose={() => setCompleteRunModalOpen(false)}
        onSubmit={handleCompleteRun}
        defaultStart={currentParameters?.taskCount || 10}
        defaultEnd={1000}
        defaultIncrement={10}
        defaultNumberOfRuns={currentParameters?.numberOfRuns ?? 5}
      />

      {/* Help Dialog */}
      <HelpDialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
        includeSflaGaBaseline={currentParameters?.includeSflaGaBaseline ?? false}
      />

      {/* Algorithms Modal */}
      <AlgorithmsModal
        open={algorithmsModalOpen}
        onClose={() => setAlgorithmsModalOpen(false)}
        includeSflaGaBaseline={currentParameters?.includeSflaGaBaseline ?? false}
      />

      <SflagaImprovementBenchmarkModal
        open={Boolean(benchmarkModalCode)}
        onClose={() => setBenchmarkModalCode(null)}
        datasetText={datasetRef.current}
        defaultTaskCount={currentParameters?.taskCount ?? 100}
        defaultNumberOfRuns={currentParameters?.numberOfRuns ?? 5}
        defaultParallelExecution={currentParameters?.parallelExecution ?? false}
        benchmarkCode={benchmarkModalCode ?? 'g'}
        onNotify={showNotification}
      />

      <NavigationSearchDialog
        open={navigationSearchOpen}
        onClose={() => setNavigationSearchOpen(false)}
        onSelect={handleNavigationSelect}
        targets={navigationTargets}
      />
    </Box>
  );
};

export default MainLayout;
